import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, KeyRound, MessageCircle, Plus, Server, ShoppingBag, Sparkles, Truck, X, type LucideIcon } from "lucide-react";
import { Button, ErrorState, Icon, LoadingState, SearchInput, SegmentedControl, TableEmpty, useToast } from "@/shared/rd";
import type { AppConfigView } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ADMIN_ROLES } from "@/lib/auth/admin-roles";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { PageHeaderActions } from "@/shared/layout/PageHeaderActions";
import { AppConfigRow } from "../components/AppConfigRow";
import { AppConfigDetails } from "@/shared/app-configs/components/AppConfigDetails";
import { AddAppConfigDialog } from "../components/AddAppConfigDialog";
import { groupConfigs, placementOf, SECTION_ORDER, type SectionId } from "../lib/configSections";
import { isChanged } from "@/shared/app-configs/lib/configValue";
import { useAppConfigsQuery } from "@/shared/app-configs/services/useAppConfigsQuery";

type Props = {
  locale: string;
};

/** The "Show" filter: every key, only those changed from their default, or only those read at start-up. */
type Show = "all" | "changed" | "boot";

const SECTION_META: Record<SectionId, { title: string; sub: string; icon: LucideIcon }> = {
  shipping: { title: keys.appConfigs.catShipping, sub: "appConfigs.subShipping", icon: Truck },
  payments: { title: keys.appConfigs.catPayments, sub: "appConfigs.subPayments", icon: CreditCard },
  messaging: { title: keys.appConfigs.catMessaging, sub: "appConfigs.subMessaging", icon: MessageCircle },
  orders: { title: keys.appConfigs.catOrder, sub: "appConfigs.subOrders", icon: ShoppingBag },
  ai: { title: keys.appConfigs.catAi, sub: "appConfigs.subAi", icon: Sparkles },
  system: { title: keys.appConfigs.catSystem, sub: "appConfigs.subSystem", icon: Server },
  custom: { title: keys.appConfigs.sectionCustom, sub: "appConfigs.subCustom", icon: KeyRound },
};

const GROUP_HEAD_STYLE = {
  padding: "10px 16px 6px",
  background: "var(--rd-surface-soft)",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--rd-text-meta)",
} as const;

/**
 * Runtime configuration, super admins only. A toolbar (search, Show filter,
 * totals), then a category list on the start side and the open category's keys
 * as compact rows, grouped by carrier / gateway. A search looks across every
 * category. A key's name, Edit or chevron opens its details drawer (value,
 * source, history); creating a custom key opens a drawer from the header.
 */
export function AppConfigsPage({ locale }: Props) {
  const navigate = useNavigate();
  const t = useTranslations();
  const toast = useToast();
  const { data: session, isLoading: isSessionLoading } = useSessionQuery();
  const isSuperAdmin = session?.admin?.roles?.includes(ADMIN_ROLES.SUPER_ADMIN) ?? false;
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [show, setShow] = useState<Show>("all");
  const [activeSection, setActiveSection] = useState<SectionId>("shipping");
  // The key the drawer shows, as it was when opened: the drawer keeps it while
  // sliding shut (and after a delete), and otherwise reads the live list.
  const [drawerSnapshot, setDrawerSnapshot] = useState<AppConfigView | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (!isSessionLoading && !isSuperAdmin) {
      navigate(`/${locale}/admin`, { replace: true });
    }
  }, [isSessionLoading, isSuperAdmin, locale, navigate]);

  const { data: configs, isLoading, error, refetch, isRefetching } = useAppConfigsQuery();

  const q = query.trim().toLowerCase();
  // Keys passing the Show filter and the search; the category list counts these.
  const matching = useMemo(
    () =>
      (configs ?? []).filter((c) => {
        if (show === "changed" && !isChanged(c)) return false;
        if (show === "boot" && c.scope !== "boot") return false;
        if (!q) return true;
        const name = (locale === "ar" ? c.nameAr : c.nameEn) ?? "";
        const description = (locale === "ar" ? c.descriptionAr : c.descriptionEn) ?? "";
        return `${name} ${c.key} ${description}`.toLowerCase().includes(q);
      }),
    [configs, show, q, locale],
  );
  const sections = useMemo(() => groupConfigs(matching), [matching]);

  const drawerConfig = drawerSnapshot ? (configs?.find((c) => c.key === drawerSnapshot.key) ?? drawerSnapshot) : null;

  if (isSessionLoading || !isSuperAdmin || isLoading) {
    return <LoadingState title={t(keys.shell.loadingTitle, { page: t("appConfigs.loadingPage") })} sub={t(keys.shell.loadingSub)} rows={6} />;
  }
  if (error || !configs) {
    return (
      <ErrorState
        title={t(keys.shell.errorTitle, { page: t("appConfigs.loadingPage") })}
        sub={t(keys.shell.errorSub)}
        onRetry={() => void refetch()}
        retryLabel={isRefetching ? t(keys.shell.retrying) : t(keys.shell.retry)}
        retrying={isRefetching}
      />
    );
  }

  const isFiltered = q !== "" || show !== "all";
  const clearFilters = () => {
    setQuery("");
    setShow("all");
  };
  const openKey = (key: string) => {
    setDrawerSnapshot(configs.find((c) => c.key === key) ?? null);
    setDrawerOpen(true);
  };
  const openCreate = () => setIsAddOpen(true);

  const changedCount = configs.filter(isChanged).length;
  const bootCount = configs.filter((c) => c.scope === "boot").length;

  // In a search the view spans every category, and each group head names its category.
  const inView = q ? sections : sections.filter((s) => s.id === activeSection);
  const rowCount = inView.reduce((sum, s) => sum + s.count, 0);
  const sectionTitle = (id: SectionId) => t(SECTION_META[id].title);
  const drawerSection = drawerConfig ? sectionTitle(placementOf(drawerConfig).section) : "";

  const empty = (() => {
    if (configs.length === 0) {
      return (
        <TableEmpty
          illustration="admin-empty-table"
          title={t("appConfigs.emptyTitle")}
          actions={
            <Button variant="primary" size="sm" icon={Plus} onClick={openCreate}>
              {t("appConfigs.newKeyAction")}
            </Button>
          }
        >
          {t("appConfigs.emptySub")}
        </TableEmpty>
      );
    }
    if (isFiltered) {
      return (
        <TableEmpty
          illustration="admin-no-results"
          title={q ? t("appConfigs.noResultsFor", { query: query.trim() }) : t("appConfigs.noResultsFiltered")}
          actions={
            <Button variant="secondary" size="sm" icon={X} onClick={clearFilters}>
              {t("appConfigs.clearSearchAndFilters")}
            </Button>
          }
        >
          {q ? t("appConfigs.noResultsSearchSub") : t("appConfigs.noResultsSub")}
        </TableEmpty>
      );
    }
    if (activeSection === "custom") {
      return (
        <TableEmpty
          illustration="admin-empty-table"
          title={t("appConfigs.customEmptyTitle")}
          actions={
            <Button variant="primary" size="sm" icon={Plus} onClick={openCreate}>
              {t("appConfigs.newKeyAction")}
            </Button>
          }
        >
          {t("appConfigs.customEmptySub")}
        </TableEmpty>
      );
    }
    return (
      <TableEmpty illustration="admin-empty-table" title={t("appConfigs.categoryEmptyTitle")}>
        {t("appConfigs.categoryEmptySub")}
      </TableEmpty>
    );
  })();

  return (
    <>
      <PageHeaderActions>
        <Button variant="primary" size="sm" icon={Plus} onClick={openCreate}>
          {t("appConfigs.newKeyAction")}
        </Button>
      </PageHeaderActions>

      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <SearchInput
          name="config-search"
          value={query}
          placeholder={t("appConfigs.searchByNameOrKey")}
          aria-label={t("appConfigs.searchByNameOrKey")}
          onChange={(event) => setQuery(event.target.value)}
          style={{ width: 280, maxWidth: "100%" }}
        />
        <SegmentedControl<Show>
          label={t("appConfigs.showFilterLabel")}
          value={show}
          onChange={setShow}
          options={[
            { value: "all", label: t(keys.appConfigs.filterAll) },
            { value: "changed", label: t("appConfigs.showChanged", { count: changedCount }) },
            { value: "boot", label: t("appConfigs.showNeedsRestart", { count: bootCount }) },
          ]}
        />
        <div style={{ flex: 1 }} />
        <span className="rd-num" style={{ fontSize: 13, color: "var(--rd-text-tertiary)" }}>
          {t("appConfigs.summary", { count: configs.length, changed: changedCount })}
        </span>
      </div>

      <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
        <nav
          aria-label={t("appConfigs.categoriesLabel")}
          style={{ flex: "0 0 220px", display: "flex", flexDirection: "column", gap: 2, position: "sticky", top: 16 }}
        >
          {SECTION_ORDER.map((id) => {
            const section = sections.find((s) => s.id === id);
            const list = section ? section.groups.flatMap((g) => g.items) : [];
            const changed = list.filter(isChanged).length;
            const current = !q && activeSection === id;
            return (
              <button
                key={id}
                type="button"
                aria-current={current ? "true" : undefined}
                onClick={() => {
                  setActiveSection(id);
                  setQuery("");
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  padding: "7px 10px",
                  border: 0,
                  borderRadius: "var(--rd-radius-md)",
                  background: current ? "var(--rd-fill-hover-strong)" : "transparent",
                  font: "inherit",
                  fontSize: 14,
                  fontWeight: current ? 600 : undefined,
                  color: "var(--rd-text)",
                  textAlign: "start",
                  cursor: "pointer",
                }}
              >
                <Icon icon={SECTION_META[id].icon} size={16} color="var(--rd-text-icon)" />
                <span style={{ flex: 1, minWidth: 0 }}>{sectionTitle(id)}</span>
                {changed > 0 ? (
                  <span
                    role="img"
                    aria-label={t("appConfigs.categoryChangedTip", { count: changed })}
                    title={t("appConfigs.categoryChangedTip", { count: changed })}
                    style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--rd-primary)" }}
                  />
                ) : null}
                <span className="rd-num" style={{ fontSize: 12, color: "var(--rd-text-meta)" }}>
                  {list.length}
                </span>
              </button>
            );
          })}
        </nav>

        <section style={{ flex: "1 1 520px", minWidth: 0, display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
              {q ? t("appConfigs.resultsFor", { query: query.trim() }) : sectionTitle(activeSection)}
            </h2>
            <span style={{ fontSize: 13, color: "var(--rd-text-secondary)" }}>
              {q ? t("appConfigs.resultsSub", { count: rowCount }) : t(SECTION_META[activeSection].sub)}
            </span>
          </div>

          {rowCount === 0 ? (
            <div className="rd-card">{empty}</div>
          ) : (
            <div className="rd-card" style={{ overflow: "hidden" }}>
              {inView.flatMap((section, si) =>
                section.groups.map((group, gi) => {
                  const firstInCard = si === 0 && gi === 0;
                  const head = q
                    ? group.label
                      ? `${sectionTitle(section.id)} · ${group.label}`
                      : sectionTitle(section.id)
                    : group.label;
                  return (
                    <Fragment key={`${section.id}:${group.label ?? ""}`}>
                      {head ? (
                        <div style={{ ...GROUP_HEAD_STYLE, borderTop: firstInCard ? undefined : "1px solid var(--rd-line-row)" }}>{head}</div>
                      ) : null}
                      {group.items.map((config, ri) => (
                        <AppConfigRow key={config.key} config={config} first={firstInCard && !head && ri === 0} onOpen={openKey} />
                      ))}
                    </Fragment>
                  );
                }),
              )}
            </div>
          )}
        </section>
      </div>

      <AppConfigDetails config={drawerConfig} open={drawerOpen && drawerConfig != null} onClose={() => setDrawerOpen(false)} sectionLabel={drawerSection} />

      <AddAppConfigDialog
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onCreated={(key) => {
          setIsAddOpen(false);
          setActiveSection("custom");
          setQuery("");
          toast.show({ message: t("appConfigs.toastCreated", { key }) });
        }}
      />
    </>
  );
}
